import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaFile } from 'react-icons/fa';
import { useProject } from '../../context/ProjectContext';
import { notify } from '../CustomToast';
import DatePickerInput from '../Common/DatePickerInput';
import TaskAttachmentsUploader from './TaskAttachmentsUploader';
import { formatFileSize } from '../../utils/uploadValidator';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useI18n } from '../../i18n';
import './TaskComposer.css';

const toDate = (str) => str ? new Date(str + 'T00:00:00') : null;
const fmtDate = (date) => date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '';

// Calculate days between two date strings (YYYY-MM-DD format)
const calculateDaysBetween = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return '';
    const start = new Date(startDateStr + 'T00:00:00');
    const end = new Date(endDateStr + 'T00:00:00');
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
    return Math.max(1, diffDays); // Minimum 1 day
};

// Check if any files are currently uploading
const hasFilesUploading = (uploadedFiles) => {
    return uploadedFiles.some(file => file.status === 'uploading');
};

const TaskComposer = ({ isOpen, onClose, task = null, projectId, initialStatus = 'todo', parentTaskId = null }) => {
    const { createTask, createSubtask, updateTask, activeProject } = useProject();
    const { t } = useI18n();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
    const fileInputRef = useRef(null);
    const { uploadedFiles, uploadFiles, removeFile, clearAll, getUploadIds } = useFileUpload();

    const isSubtask = !!parentTaskId || !!task?.parent_task_id;
    const isEditing = !!task;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        status: 'todo',
        expected_days: '',
        start_date: '',
        end_date: '',
    });

    const [errors, setErrors] = useState({});
    const [uploadedAttachments, setUploadedAttachments] = useState([]);

    // Get team members for assignment
    const teamMembers = activeProject?.team?.members || [];

    // Reset form when modal opens/closes or task changes
    useEffect(() => {
        if (isOpen) {
            if (task) {
                // Safely convert values to strings (in case API returns objects)
                const safeValue = (val, defaultVal) => {
                    if (typeof val === 'string') return val;
                    if (typeof val === 'number') return String(val);
                    if (val && typeof val === 'object' && val.value) return String(val.value);
                    return defaultVal;
                };

                setFormData({
                    title: String(task.title || ''),
                    description: String(task.description || ''),
                    assigned_to: safeValue(task.assigned_to, ''),
                    priority: safeValue(task.priority, 'medium'),
                    status: safeValue(task.status, 'todo'),
                    expected_days: safeValue(task.expected_days, ''),
                    start_date: String(task.start_date || ''),
                    end_date: String(task.end_date || ''),
                });
            } else {
                // Use local date, not UTC to avoid timezone issues
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const today = `${year}-${month}-${day}`;

                setFormData({
                    title: '',
                    description: '',
                    assigned_to: '',
                    priority: 'medium',
                    status: initialStatus || 'todo',
                    expected_days: '',
                    start_date: today,
                    end_date: '',
                });
            }
            setErrors({});
            setUploadedAttachments([]);
            clearAll(); // Clear uploaded files from hook
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isOpen, task, clearAll]);

    // Handle ESC key
    useEffect(() => {
        if (isOpen) {
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    onClose();
                }
            };
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
            };
        }
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleFileSelect = async (e) => {
        const files = e.target.files;
        if (!files) return;

        // Immediately upload files on selection (not on form submit)
        // The useFileUpload hook handles validation and uploads each file
        // Backend's DeduplicationService handles hash-based deduplication
        await uploadFiles(files);

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        try {
            setIsSubmitting(true);

            // Validate required fields first
            if (!formData.title || !formData.title.trim()) {
                setErrors({ title: ['Task name is required'] });
                setIsSubmitting(false);
                return;
            }

            if (!formData.assigned_to) {
                setErrors({ assigned_to: ['Assigning a member is required'] });
                setIsSubmitting(false);
                return;
            }

            if (!formData.expected_days) {
                setErrors({ expected_days: ['Expected days is required'] });
                setIsSubmitting(false);
                return;
            }

            if (!formData.start_date) {
                setErrors({ start_date: ['Start date is required'] });
                setIsSubmitting(false);
                return;
            }

            if (!formData.end_date) {
                setErrors({ end_date: ['End date is required'] });
                setIsSubmitting(false);
                return;
            }

            // Get today's date for validation (use local date, not UTC)
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;

            // Validate start_date is not before today
            if (formData.start_date < today) {
                setErrors({ start_date: ['Start date cannot be before today'] });
                setIsSubmitting(false);
                return;
            }

            // Validate end_date is not before start_date
            if (formData.end_date < formData.start_date) {
                setErrors({ end_date: ['End date cannot be before start date'] });
                setIsSubmitting(false);
                return;
            }

            // Helper to safely convert values to string, handling objects
            const safeString = (value, defaultValue = '') => {
                if (typeof value === 'string') return value.trim();
                if (typeof value === 'number') return String(value).trim();
                if (value && typeof value === 'object') {
                    // Log which field has the object issue
                    console.error('Object value detected:', { value, type: typeof value, keys: Object.keys(value || {}) });
                    return defaultValue;
                }
                return String(value || defaultValue).trim();
            };

            // Build clean data object with ONLY serializable primitive values
            const cleanData = {
                title: safeString(formData.title),
                description: safeString(formData.description),
                assigned_to: parseInt(safeString(formData.assigned_to, '0'), 10),
                priority: safeString(formData.priority, 'medium'),
                status: safeString(formData.status, 'todo'),
                expected_days: parseInt(safeString(formData.expected_days, '1'), 10),
                start_date: safeString(formData.start_date),
                end_date: safeString(formData.end_date)
            };

            // Add parent_task_id if creating a subtask
            if (parentTaskId && !isEditing) {
                cleanData.parent_task_id = parentTaskId;
            }

            // Log form data for debugging
            console.log('Form submission:', {
                formDataStatus: formData.status,
                formDataPriority: formData.priority,
                cleanDataStatus: cleanData.status,
                cleanDataPriority: cleanData.priority
            });

            // Validate clean data doesn't have object values
            if (cleanData.status === '' || cleanData.status === 'medium' && formData.status !== 'medium') {
                console.error('Failed to extract valid status from:', formData.status);
                notify('error', 'Invalid status selected - please select again');
                return;
            }
            if (cleanData.priority === '' || cleanData.priority === 'todo' && formData.priority !== 'todo') {
                console.error('Failed to extract valid priority from:', formData.priority);
                notify('error', 'Invalid priority selected - please select again');
                return;
            }

            // Double check data is serializable before sending
            try {
                JSON.stringify(cleanData);
            } catch (serializeError) {
                console.error('Data serialization failed:', serializeError, cleanData);
                notify('error', 'Invalid form data - please refresh and try again');
                return;
            }

            let newTaskId = null;

            if (isEditing) {
                // For edit mode, TaskAttachmentsUploader handles attachments separately
                await updateTask(task.id, cleanData);
                newTaskId = task.id;
            } else {
                // For create mode, files were already uploaded on file selection (onChange)
                // Get the attachment IDs from the hook
                const attachmentIds = getUploadIds();

                // Add attachment IDs to cleanData if there are any
                if (attachmentIds.length > 0) {
                    cleanData.attachment_ids = attachmentIds;
                }

                // Create task or subtask with attachment IDs in payload
                // Backend will link the uploaded temp files to this new task/subtask
                if (parentTaskId) {
                    // Creating a subtask
                    const response = await createSubtask(parentTaskId, cleanData);
                    newTaskId = response?.id;
                } else {
                    // Creating a regular task
                    const response = await createTask(projectId || activeProject?.id, cleanData);
                    newTaskId = response?.data?.task?.id || response?.data?.id;
                }
            }

            onClose();
        } catch (error) {
            console.error('Task submission error:', error);
            // Handle validation errors from API
            if (error?.status === 422) {
                setErrors(error.errors || {});
            } else if (error?.response?.data?.data) {
                setErrors(error.response.data.data);
            } else if (error?.status === 400) {
                // Catch the circular reference error from API interceptor
                console.error('Bad request:', error);
            } else {
                console.error('Unexpected error:', error);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="task-composer-overlay" onClick={handleBackdropClick}>
            <div className="task-composer-modal" role="dialog" aria-modal="true">
                {/* Header */}
                <div className="task-composer-header">
                    <h2 className="task-composer-title">
                        {isEditing 
                            ? (isSubtask ? t('legacy.edit_sub_task') : t('legacy.edit_task'))
                            : (isSubtask ? t('legacy.create_sub_task') : t('legacy.create_task'))
                        }
                    </h2>
                    <button
                        type="button"
                        className="task-composer-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className="task-composer-body">
                    <form id="task-form" onSubmit={handleSubmit} noValidate>

                        {/* Task Name */}
                        <div className="task-form-group">
                            <label className="task-form-label">
                                {t('legacy.task_name')}<span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={`task-form-input ${errors.title ? 'error' : ''}`}
                                placeholder={t('legacy.enter_task_name')}
                                autoFocus
                            />
                            {errors.title && <div className="task-form-error">{Array.isArray(errors.title) ? errors.title[0] : errors.title}</div>}
                        </div>

                        {/* Task Description */}
                        <div className="task-form-group">
                            <label className="task-form-label">
                                {t('legacy.task_description')}<span className="required">*</span>
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className={`task-form-textarea ${errors.description ? 'error' : ''}`}
                                placeholder={t('legacy.enter_task_description')}
                            />
                            {errors.description && <div className="task-form-error">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</div>}
                        </div>

                        {/* Assign a Member & Priority Row */}
                        <div className="task-form-row">
                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.assign_a_member')}<span className="required">*</span>
                                </label>
                                <select
                                    name="assigned_to"
                                    value={formData.assigned_to}
                                    onChange={handleChange}
                                    className={`task-form-select ${errors.assigned_to ? 'error' : ''}`}
                                >
                                    <option value="">{t('legacy.select_a_member')}</option>
                                    {teamMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.assigned_to && <div className="task-form-error">{Array.isArray(errors.assigned_to) ? errors.assigned_to[0] : errors.assigned_to}</div>}
                            </div>

                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.priority_label')}:<span className="required">*</span>
                                </label>
                                <select
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleChange}
                                    className={`task-form-select ${errors.priority ? 'error' : ''}`}
                                >
                                    <option value="low">{t('legacy.priority_low')}</option>
                                    <option value="medium">{t('legacy.priority_medium')}</option>
                                    <option value="high">{t('legacy.priority_high')}</option>
                                    <option value="urgent">{t('legacy.priority_urgent')}</option>
                                </select>
                                {errors.priority && <div className="task-form-error">{Array.isArray(errors.priority) ? errors.priority[0] : errors.priority}</div>}
                            </div>
                        </div>

                        {/* Attachments */}
                        {isEditing ? (
                            <TaskAttachmentsUploader
                                taskId={task?.id}
                                onAttachmentsAdded={(attachments) => {
                                    setUploadedAttachments(prev => [...prev, ...attachments]);
                                }}
                                onUploadStatusChange={(isUploading) => {
                                    setIsUploadingAttachments(isUploading);
                                }}
                                disabled={isSubmitting}
                            />
                        ) : (
                            <div className="task-form-attachments">
                                <div className="attachments-header">
                                    <label className="task-form-label">{t('legacy.attachments_optional')}</label>
                                    <button
                                        type="button"
                                        className="btn-add-files-create"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isSubmitting}
                                    >
                                        {t('legacy.add_files_create')}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileSelect}
                                        disabled={isSubmitting}
                                        style={{ display: 'none' }}
                                    />
                                </div>

                                {uploadedFiles.length > 0 && (
                                    <div className="selected-files-list-create">
                                        {uploadedFiles.map((fileData) => (
                                            <div key={fileData.id} className={`file-item-create ${fileData.status}`}>
                                                <FaFile size={14} style={{ color: '#0c5097' }} />
                                                <div className="file-info-create">
                                                    <span className="file-name-create">{fileData.name}</span>
                                                    <span className="file-size-create">{formatFileSize(fileData.size)}</span>
                                                    <span className="file-status-create">
                                                        {fileData.status === 'uploading' && '(uploading...)'}
                                                        {fileData.status === 'uploaded' && '(uploaded)'}
                                                        {fileData.status === 'error' && `(error: ${fileData.error})`}
                                                    </span>
                                                </div>
                                                {fileData.status === 'uploading' && (
                                                    <div className="upload-progress-create">
                                                        <div className="progress-bar" style={{ width: `${fileData.progress}%` }}></div>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn-remove-create"
                                                    onClick={() => removeFile(fileData.id, fileData.uploadId)}
                                                    disabled={isSubmitting || fileData.status === 'uploading'}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Status & Expected Days Row */}
                        <div className="task-form-row">
                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.status_heading')}:<span className="required">*</span>
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className={`task-form-select ${errors.status ? 'error' : ''}`}
                                >
                                    <option value="todo">{t('legacy.status_todo')}</option>
                                    <option value="in_progress">{t('legacy.status_in_progress')}</option>
                                    <option value="done">{t('legacy.status_done')}</option>
                                    <option value="on_hold">{t('legacy.status_on_hold')}</option>
                                    <option value="reopen">{t('legacy.status_reopen')}</option>
                                </select>
                                {errors.status && <div className="task-form-error">{Array.isArray(errors.status) ? errors.status[0] : errors.status}</div>}
                            </div>

                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.expected_days')}<span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="expected_days"
                                    value={formData.expected_days}
                                    onChange={handleChange}
                                    className={`task-form-input ${errors.expected_days ? 'error' : ''}`}
                                    min="1"
                                    readOnly
                                    title={t('legacy.expected_days')}
                                />
                                {errors.expected_days && <div className="task-form-error">{Array.isArray(errors.expected_days) ? errors.expected_days[0] : errors.expected_days}</div>}
                            </div>
                        </div>

                        {/* Start Date & End Date Row */}
                        <div className="task-form-row">
                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.start_date_label')}<span className="required">*</span>
                                </label>
                                <DatePickerInput
                                    selected={toDate(formData.start_date)}
                                    onChange={(date) => {
                                        const newStartDate = fmtDate(date);
                                        const newEndDate = formData.end_date || newStartDate;
                                        const calculatedDays = calculateDaysBetween(newStartDate, newEndDate);

                                        setFormData(prev => ({
                                            ...prev,
                                            start_date: newStartDate,
                                            // Auto-fill end_date with start_date if end_date is empty
                                            end_date: newEndDate,
                                            // Auto-calculate expected_days
                                            expected_days: calculatedDays
                                        }));
                                        if (errors.start_date) setErrors(prev => ({ ...prev, start_date: null }));
                                    }}
                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                    placeholder={t('legacy.start_date_label')}
                                    isInvalid={!!errors.start_date}
                                />
                                {errors.start_date && <div className="task-form-error">{Array.isArray(errors.start_date) ? errors.start_date[0] : errors.start_date}</div>}
                            </div>

                            <div className="task-form-group">
                                <label className="task-form-label">
                                    {t('legacy.end_date_label')}<span className="required">*</span>
                                </label>
                                <DatePickerInput
                                    selected={toDate(formData.end_date)}
                                    onChange={(date) => {
                                        const newEndDate = fmtDate(date);
                                        const calculatedDays = calculateDaysBetween(formData.start_date, newEndDate);

                                        setFormData(prev => ({
                                            ...prev,
                                            end_date: newEndDate,
                                            // Auto-calculate expected_days
                                            expected_days: calculatedDays
                                        }));
                                        if (errors.end_date) setErrors(prev => ({ ...prev, end_date: null }));
                                    }}
                                    minDate={toDate(formData.start_date) || new Date(new Date().setHours(0, 0, 0, 0))}
                                    placeholder={t('legacy.end_date_label')}
                                    isInvalid={!!errors.end_date}
                                />
                                {errors.end_date && <div className="task-form-error">{Array.isArray(errors.end_date) ? errors.end_date[0] : errors.end_date}</div>}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="task-composer-footer">
                    <button
                        type="button"
                        className="task-composer-btn task-composer-btn-secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('legacy.cancel_btn')}
                    </button>
                    <button
                        type="submit"
                        form="task-form"
                        className="task-composer-btn task-composer-btn-primary task-composer-submit-btn"
                        disabled={isSubmitting || hasFilesUploading(uploadedFiles) || isUploadingAttachments}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border" role="status" aria-hidden="true"></span>
                                <span>{t('legacy.saving')}</span>
                            </>
                        ) : isEditing ? t('legacy.update_task') : (isSubtask ? t('legacy.create_sub_task') : t('legacy.create_task'))}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TaskComposer;
